import { useQuery } from '@tanstack/react-query';
import { getPivotSignals, PivotSignalsResponse } from '../../../api/metrics';

/**
 * Hook to fetch pivot interaction signals
 * Filters signals to match the specified timeframe
 */
export function useInteractionSignals(
  productId: string,
  startDate: Date,
  endDate: Date,
  minStrength: number = 0.5,
  enabled: boolean = true,
  timeframe?: string
) {
  return useQuery<PivotSignalsResponse, Error>({
    queryKey: ['pivotSignals', productId, startDate.toISOString(), endDate.toISOString(), minStrength, timeframe],
    queryFn: async () => {
      const response = await getPivotSignals(
        productId,
        startDate.toISOString(),
        endDate.toISOString(),
        minStrength
      );

      // Filter signals to match the current timeframe if specified
      if (timeframe && response.signals) {
        const filteredSignals = response.signals.filter(signal => {
          // Match the timeframe - handle both formats (THIRTY_MIN and 30M, etc.)
          const signalTimeframe = signal.timeframe?.toUpperCase() || '';
          const chartTimeframe = timeframe.toUpperCase();

          // Exact match first
          if (signalTimeframe === chartTimeframe) return true;

          // Create comprehensive mapping for all timeframes
          const timeframeMap: Record<string, string[]> = {
            'THIRTY_MIN': ['30M', '30MIN', 'THIRTY_MIN'],
            'ONE_HOUR': ['1H', '1HOUR', 'ONE_HOUR'],
            'TWO_HOUR': ['2H', '2HOUR', 'TWO_HOUR'],
            'FOUR_HOUR': ['4H', '4HOUR', 'FOUR_HOUR'],
            'EIGHT_HOUR': ['8H', '8HOUR', 'EIGHT_HOUR'],
            'TWELVE_HOUR': ['12H', '12HOUR', 'TWELVE_HOUR'],
            'TWENTY_FOUR_HOUR': ['24H', '24HOUR', 'TWENTY_FOUR_HOUR', 'DAILY', '1D'],
            'DAILY': ['24H', '24HOUR', 'TWENTY_FOUR_HOUR', 'DAILY', '1D'],
            'SEVEN_DAY': ['7D', '7DAY', 'SEVEN_DAY', 'WEEKLY', '1W'],
            'WEEKLY': ['7D', '7DAY', 'SEVEN_DAY', 'WEEKLY', '1W'],
            'THIRTY_DAY': ['30D', '30DAY', 'THIRTY_DAY', 'MONTHLY', '1M'],
            'MONTHLY': ['30D', '30DAY', 'THIRTY_DAY', 'MONTHLY', '1M'],
          };

          // Check if signal timeframe matches any of the chart timeframe's aliases
          const chartAliases = timeframeMap[chartTimeframe] || [];
          if (chartAliases.includes(signalTimeframe)) return true;

          // Check reverse - if signal timeframe has aliases that match chart timeframe
          const signalAliases = timeframeMap[signalTimeframe] || [];
          if (signalAliases.includes(chartTimeframe)) return true;

          return false;
        });

        return {
          ...response,
          signals: filteredSignals,
        };
      }

      return response;
    },
    enabled: enabled && !!productId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
  });
}
